/**
 * A v4 UUID that also works on a NON-SECURE origin.
 *
 * `crypto.randomUUID()` is secure-context only: on `http://192.168.1.88` — an
 * on-premise install served over plain HTTP on a LAN — it is `undefined`, and
 * calling it throws `TypeError: crypto.randomUUID is not a function`.
 *
 * That is not a theoretical edge. `getDeviceId()` runs inside the axios REQUEST
 * INTERCEPTOR, so the throw happened before any request was sent, and axios
 * rejected with an error carrying no HTTP status. The login screen maps
 * "no status" to "Couldn't reach the server, so your details were not
 * checked" — a network diagnosis for a client-side TypeError, pointing at the
 * one place the fault was not. Every request from that browser failed the same
 * way, so the install looked dead while the API was answering 200s.
 *
 * `crypto.getRandomValues()` has no such restriction — it is available on
 * insecure origins — so the fallback is still cryptographically random, not
 * `Math.random()`. The bit-twiddling sets the version (4) and variant (10x)
 * fields per RFC 4122 §4.4, which is exactly what `randomUUID` guarantees.
 */
export function randomUuid(): string {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  // Version 4 in the high nibble of byte 6; variant 10x in the top bits of
  // byte 8. Indices are in range by construction (the array is 16 bytes).
  bytes[6] = ((bytes[6] as number) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] as number) & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
}
